import { clamp } from 'lodash-es';
import {
  BoxGeometry,
  BufferAttribute,
  Color,
  type ColorRepresentation,
  Mesh,
  MeshStandardMaterial,
  type QuaternionTuple,
  type Vector3Tuple,
} from 'three';
import { Line2, LineGeometry, LineMaterial } from 'three-stdlib';

import { TOOL_CONST } from '@/constant';
import type { BoxWithLabel } from '@/type';
import { quaternionToEuler } from '@/util/math';

import { COLOR, TOOL_LABELING_BOX } from './constant';

// 1x1x1 박스의 8개 vertices (중심 기준)
const UNIT_BOX_VERTEX_LIST: [number, number, number][] = [
  [-0.5, -0.5, -0.5],
  [0.5, -0.5, -0.5],
  [-0.5, 0.5, -0.5],
  [0.5, 0.5, -0.5],
  [0.5, -0.5, 0.5],
  [-0.5, -0.5, 0.5],
  [0.5, 0.5, 0.5],
  [-0.5, 0.5, 0.5],
];

// 박스의 edge를 정의하는 vertex index 쌍
const EDGE_INDEX_LIST: [number, number][] = [
  [0, 1],
  [1, 3],
  [3, 2],
  [2, 0],
  [0, 5],
  [5, 4],
  [4, 6],
  [6, 7],
  [7, 5],
  [4, 1],
  [3, 6],
  [7, 2],
];

// line color 배열 크기 (24 edges * 3 rgb)
const LINE_COLOR_SIZE = 72;

// 재사용 가능한 Color 인스턴스
const tempColor = new Color();

export class LabelingBox {
  public boxGeometry = new BoxGeometry(1, 1, 1);
  public boxMaterial = new MeshStandardMaterial({
    depthWrite: false,
    opacity: 0.1,
    transparent: true,
    vertexColors: true,
  });
  public boxMesh!: Mesh;
  public colorBuffer!: Float32Array;
  public faceColorList: Color[] = Array.from(
    { length: 8 * 3 },
    () => COLOR.DEFAULT_BOX,
  );
  public line2!: Line2;
  public lineGeometry = new LineGeometry();
  public lineMaterial = new LineMaterial({
    linewidth: TOOL_LABELING_BOX.LINE.DEFAULT,
    opacity: TOOL_LABELING_BOX.OPACITY.LINE.DEFAULT,
    transparent: true,
    vertexColors: true,
  });

  private _isDisposed = false;
  private _isSelected = false;

  constructor(
    public color: string | undefined,
    public confidence: number | undefined,
    public dimension: Vector3Tuple,
    public id: string,
    public label: string,
    public position: Vector3Tuple,
    public quaternion: QuaternionTuple,
    public trackId: string | undefined,
    public workType: TOOL_CONST.TOOL_LABELING_BOX_WORK_TYPE | undefined,
  ) {
    this._init();

    this.updateColor(this.color ?? COLOR.DEFAULT_BOX);
    this.updatePosition(this.position);
    this.updateDimension(this.dimension);
    this.updateQuaternion(this.quaternion);
  }

  deselect = () => {
    this.boxMaterial.opacity =
      this.workType === TOOL_CONST.TOOL_LABELING_BOX_WORK_TYPE.REVIEW
        ? TOOL_LABELING_BOX.OPACITY.BOX.REVIEW
        : TOOL_LABELING_BOX.OPACITY.BOX.DEFAULT;
    this.lineMaterial.opacity = TOOL_LABELING_BOX.OPACITY.LINE.DEFAULT;
    this.lineMaterial.linewidth = TOOL_LABELING_BOX.LINE.DEFAULT;

    this._isSelected = false;
  };

  /**
   * 리소스 정리 - GPU 메모리 해제
   * 객체를 완전히 폐기할 때만 호출
   */
  dispose = () => {
    if (this._isDisposed) return;

    this.boxGeometry.dispose();
    this.boxMaterial.dispose();

    this.colorBuffer = new Float32Array();

    this.faceColorList = [];

    this.lineGeometry.dispose();
    this.lineMaterial.dispose();

    this._isDisposed = true;
  };

  getRotation = () => {
    const [x, y, z] = quaternionToEuler(this.quaternion);

    return [x, y, z] as Vector3Tuple;
  };

  getShortenTrackId = () => {
    if (
      !this.trackId ||
      typeof this.trackId !== 'string' ||
      this.trackId.length < 19
    )
      return;

    return this.trackId.slice(-4).replace(/^0+/, '');
  };

  isSelected = () => this._isSelected;

  /**
   * 객체 재사용을 위한 상태 초기화
   * Geometry/Material은 유지하고 데이터만 갱신
   */
  reset = (data: {
    color?: string;
    confidence?: number;
    dimension: Vector3Tuple;
    id: string;
    label: string;
    position: Vector3Tuple;
    quaternion: QuaternionTuple;
    trackId?: string;
    workType?: TOOL_CONST.TOOL_LABELING_BOX_WORK_TYPE;
  }) => {
    if (this._isDisposed) {
      throw new Error('Cannot reset a disposed LabelingBox');
    }

    this.confidence = data.confidence;
    this.id = data.id;
    this.label = data.label;
    this.trackId = data.trackId;
    this.workType = data.workType;

    this.deselect();

    this._initBoxMesh();
    this._initLine2();

    this.updateColor(data.color ?? COLOR.DEFAULT_BOX);
    this.updatePosition(data.position);
    this.updateDimension(data.dimension);
    this.updateQuaternion(data.quaternion);

    return this;
  };

  select = () => {
    this.boxMaterial.opacity =
      this.workType === TOOL_CONST.TOOL_LABELING_BOX_WORK_TYPE.REVIEW
        ? TOOL_LABELING_BOX.OPACITY.BOX.REVIEW
        : TOOL_LABELING_BOX.OPACITY.BOX.SELECTED;
    this.lineMaterial.opacity = TOOL_LABELING_BOX.OPACITY.LINE.SELECTED;
    this.lineMaterial.linewidth = TOOL_LABELING_BOX.LINE.SELECTED;

    this._isSelected = true;
  };

  setLineColorByIndex = (index: number, newColor: string | number | Color) => {
    const startAttr = this.lineGeometry.getAttribute('instanceColorStart');
    const endAttr = this.lineGeometry.getAttribute('instanceColorEnd');

    if (!startAttr || !endAttr) {
      console.warn('lineGeometry has not color attribute');
      return;
    }

    const color = tempColor.set(newColor);

    startAttr.setXYZ(index, color.r, color.g, color.b);
    endAttr.setXYZ(index, color.r, color.g, color.b);

    startAttr.needsUpdate = true;
    endAttr.needsUpdate = true;
  };

  toJSON = () => ({
    color: this.color,
    confidence: this.confidence,
    dimension: this.dimension,
    id: this.id,
    label: this.label,
    position: this.position,
    quaternion: this.quaternion,
    rotation: this.getRotation(),
    trackId: this.trackId,
    workType: this.workType,
  });

  toJSONForMiri = () => {
    const { color: _, quaternion: __, workType: ___, ...rest } = this.toJSON();

    return rest as Omit<BoxWithLabel, 'color' | 'quaternion' | 'workType'>;
  };

  updateColor = (color: ColorRepresentation) => {
    const resolvedColor = this.boxMaterial.color.set(color) as Color;

    const colorArray = Array.from({ length: LINE_COLOR_SIZE }, (_, i) => {
      const component = i % 3;

      if (component === 0) return resolvedColor.r;
      if (component === 1) return resolvedColor.g;
      return resolvedColor.b;
    });

    this.lineGeometry.setColors(colorArray);

    const colorAttr =
      this.lineGeometry.attributes.instanceColor ||
      this.lineGeometry.attributes.color;

    if (colorAttr) colorAttr.needsUpdate = true;

    this.color = `#${resolvedColor.getHexString()}`;
  };

  updateBoxOpacity = (opacity: number) => {
    this.boxMaterial.opacity = clamp(
      opacity,
      TOOL_LABELING_BOX.OPACITY.BOX.MIN,
      TOOL_LABELING_BOX.OPACITY.BOX.MAX,
    );
  };

  updateLineOpacity = (opacity: number) => {
    this.lineMaterial.opacity = clamp(
      opacity,
      TOOL_LABELING_BOX.OPACITY.LINE.MIN,
      TOOL_LABELING_BOX.OPACITY.LINE.MAX,
    );
  };

  updateDimension = (dimension: Vector3Tuple) => {
    this._assertInitialized();

    this.dimension = dimension;

    // mesh scale로 dimension 적용
    this.boxMesh.scale.set(...dimension);
    this.line2.scale.set(...dimension);

    this.lineGeometry.computeBoundingSphere();
    this.lineGeometry.computeBoundingBox();
  };

  updatePosition = (position: Vector3Tuple) => {
    this._assertInitialized();

    this.boxMesh.position.set(...position);
    this.line2.position.set(...position);

    this.position = position;

    this.lineGeometry.computeBoundingSphere();
  };

  updateQuaternion = (quaternion: QuaternionTuple) => {
    this.boxMesh.quaternion.set(...quaternion);
    this.line2.quaternion.set(...quaternion);

    this.quaternion = quaternion;
  };

  private _assertInitialized = () => {
    if (!this.boxMesh) {
      throw new Error('boxMesh is null');
    }

    if (!this.line2) {
      throw new Error('line2 is null');
    }
  };

  private _init = () => {
    this._initBoxMesh();
    this._initLine2();
  };

  private _initBoxMesh = () => {
    this.colorBuffer = new Float32Array(this.faceColorList.length * 3);

    this.faceColorList.forEach((color, i) => {
      this.colorBuffer[i * 3 + 0] = color.r;
      this.colorBuffer[i * 3 + 1] = color.g;
      this.colorBuffer[i * 3 + 2] = color.b;
    });

    this.boxGeometry.setAttribute(
      'color',
      new BufferAttribute(this.colorBuffer, 3),
    );

    this.boxMesh = new Mesh(this.boxGeometry, this.boxMaterial);
    this.boxMesh.userData.id = this.id;
    this.boxMesh.userData.trackId = this.trackId;
  };

  private _initLine2 = () => {
    // 1x1x1 단위 박스의 edge positions 설정 (scale로 실제 dimension 적용)
    const linePositionList = EDGE_INDEX_LIST.flatMap(([start, end]) => [
      ...UNIT_BOX_VERTEX_LIST[start],
      ...UNIT_BOX_VERTEX_LIST[end],
    ]);

    this.lineGeometry.setPositions(linePositionList);
    this.line2 = new Line2(this.lineGeometry, this.lineMaterial);
    this.line2.userData.id = this.id;
    this.line2.userData.trackId = this.trackId;
  };
}

export class LabelingBoxBuilder {
  private color?: string;
  private confidence?: number;
  private dimension?: Vector3Tuple;
  private id?: string;
  private label?: string;
  private position?: Vector3Tuple;
  private quaternion?: QuaternionTuple;
  private trackId?: string;
  private workType?: TOOL_CONST.TOOL_LABELING_BOX_WORK_TYPE;

  build = () => {
    if (
      !this.dimension ||
      !this.id ||
      !this.label ||
      !this.position ||
      !this.quaternion
    ) {
      return null;
    }

    return new LabelingBox(
      this.color,
      this.confidence,
      this.dimension,
      this.id,
      this.label,
      this.position,
      this.quaternion,
      this.trackId,
      this.workType,
    );
  };

  reset = () => {
    this.color = '#ffffff';
    this.confidence = 0;
    this.dimension = [0, 0, 0];
    this.id = '';
    this.label = '';
    this.position = [0, 0, 0];
    this.quaternion = [0, 0, 0, 1];
    this.trackId = '';
    this.workType = TOOL_CONST.TOOL_LABELING_BOX_WORK_TYPE.WORK;

    return this;
  };

  setColor = (color?: string) => {
    this.color = color;
    return this;
  };

  setConfidence = (confidence?: number) => {
    this.confidence = confidence;
    return this;
  };

  setDimension = (dimension: Vector3Tuple) => {
    this.dimension = dimension;
    return this;
  };

  setId = (id: string) => {
    this.id = id;
    return this;
  };

  setLabel = (label: string) => {
    this.label = label;
    return this;
  };

  setPosition = (position: Vector3Tuple) => {
    this.position = position;
    return this;
  };

  setQuaternion = (quaternion: QuaternionTuple) => {
    this.quaternion = quaternion;
    return this;
  };

  setTrackId = (trackId?: string) => {
    this.trackId = trackId;
    return this;
  };

  setWorkType = (workType?: TOOL_CONST.TOOL_LABELING_BOX_WORK_TYPE) => {
    this.workType = workType;
    return this;
  };
}
